import cx from 'classnames';
import styles from './LotteryGameRoomHead.module.scss';

import { FC, memo } from 'react';

import { twoNumsSeconds } from '@utils/time';
import { CendIcon } from '@assets/icons/CendIcon';
import { TimerIcon, XmarkIcon } from '@assets/icons';
import { IconColor } from '@assets/types';
import { AnimatedDotsText } from '@shared/AnimatedDotsText';
import { WinnersTextWithStars } from '@shared/WinnersTextWithStars';
import { FairPlayMark } from '@shared/FairPlayMark';
import { Typography } from '@shared/Typography';
import { RawButton } from '@shared/RawButton';
import {
  getTicketLabelNode,
  isMetallicTicket,
} from '@features/LotteryGame/helpers/ticketNumberUtils';
import { SuperTicketType } from '@features/LotteryGame/types';
import { useTranslation } from 'react-i18next';

interface LotteryGameRoomHeadProps {
  gameStarted: boolean;
  gameFinished: boolean;
  gameIndex: number;
  gameBet: string;
  elapsedTime?: number;
  animationComplete: boolean;
  showCloseButton: boolean;
  showFairPlay: VoidFunction;
  onCloseGame: VoidFunction;
  lostConnection: boolean;
  luckyTicketNumber?: string;
  ticketNumber?: string;
}

export const LotteryGameRoomHead: FC<LotteryGameRoomHeadProps> = memo((props) => {
  const {
    gameStarted,
    gameFinished,
    gameBet,
    elapsedTime,
    gameIndex,
    animationComplete,
    showCloseButton,
    showFairPlay,
    onCloseGame,
    lostConnection,
    luckyTicketNumber,
    ticketNumber,
  } = props;

  const { t } = useTranslation('mainPage');

  return (
    <div
      className={cx(styles.lgrHeader, {
        [styles.lgrHeaderLostConnection]: lostConnection,
      })}
    >
      {!gameStarted && !lostConnection && <AnimatedDotsText text={t('waitingplayers')} />}
      {gameStarted && !lostConnection && (
        <div
          className={cx(styles.lgrHeaderTitle, {
            [styles.lgrHeaderTitleFinished]: gameFinished && animationComplete,
          })}
        >
          <TableInfo
            gameFinished={gameFinished}
            gameIndex={gameIndex}
            gameBet={gameBet}
            onShowFairPlay={showFairPlay}
            luckyTicketNumber={luckyTicketNumber}
            ticketNumber={ticketNumber}
          />

          {gameFinished && (
            <>
              {animationComplete ? (
                <WinnersTextWithStars luckyTicketNumber={luckyTicketNumber} />
              ) : (
                <AnimatedDotsText text={t('spotWinNumbers')} />
              )}
            </>
          )}
          {!gameFinished && <Timer elapsedTime={elapsedTime} />}
        </div>
      )}

      {showCloseButton && (
        <RawButton onClick={onCloseGame} className={styles.lgrCloseButton}>
          <XmarkIcon width={15} height={15} />
        </RawButton>
      )}
    </div>
  );
});

interface TableInfoProps {
  gameFinished: boolean;
  gameIndex: number;
  gameBet: string;
  onShowFairPlay: VoidFunction;
  luckyTicketNumber?: string;
  ticketNumber?: string;
}

const TableInfo: FC<TableInfoProps> = memo((props) => {
  const { gameFinished, gameBet, onShowFairPlay, luckyTicketNumber, ticketNumber } =
    props;

  return (
    <div className={styles.lgrFairPlayContainer}>
      <FairPlayMark onClick={onShowFairPlay} />

      {!gameFinished && (
        <div className={styles.lgrhTicketInfo}>
          <Typography
            component="h3"
            textTransform="uppercase"
            className={cx({
              [styles.lgrhNumber]:
                luckyTicketNumber &&
                !isMetallicTicket(luckyTicketNumber as SuperTicketType),
            })}
          >
            {getTicketLabelNode(luckyTicketNumber, ticketNumber)}
          </Typography>
          <div
            className={cx({
              [styles.lgrhNumber]:
                luckyTicketNumber &&
                !isMetallicTicket(luckyTicketNumber as SuperTicketType),
            })}
          >
            (<CendIcon width={7} height={14} className={styles.lgrhCentIcon} /> {gameBet})
          </div>
        </div>
      )}
    </div>
  );
});

interface TimerProps {
  elapsedTime?: number;
}

const Timer: FC<TimerProps> = memo((props) => {
  const { elapsedTime } = props;

  const formattedTime = elapsedTime ? `00:${twoNumsSeconds(elapsedTime)}` : '00:00';
  const color = elapsedTime ? IconColor.GREEN : IconColor.LOSE;
  const className = elapsedTime ? styles.lgrhTime : styles.lgrhTimeNull;

  return (
    <div className={styles.lgrhTimer}>
      <TimerIcon className={styles.lgrElapsedAnimated} fillColor={color} />
      <Typography component="h3" className={className}>
        {formattedTime}
      </Typography>
    </div>
  );
});
